/**
 * glasswork.js — theme-aware runtime resolver for the site's inline styles
 * and canvas fills (a `var(--x)` string can't be assigned to `ctx.fillStyle`).
 *
 * `THEME.*` is refreshed from the `--gw-*` CSS variables (glasswork.css) on
 * load and whenever `<html data-theme>` flips. Those `--gw-*` are now aliases
 * onto @quantum/tokens, so this object tracks the shared token palette.
 *
 * The DEFAULTS below are only the pre-refresh fallback (SPA, no SSR — the
 * synchronous refreshTheme() at module load almost always wins first). They
 * mirror the dark token values so a first paint before refresh matches.
 */

const DEFAULTS = {
  bgOuter: '#05070E', // --void
  bgPage:
    'radial-gradient(ellipse 60% 50% at 12% -8%, rgba(44,245,167,0.06), transparent 60%), ' +
    'radial-gradient(ellipse 50% 40% at 88% 10%, rgba(139,124,255,0.05), transparent 60%), ' +
    'radial-gradient(ellipse at top left, #0B101E 0%, #05070E 70%)',
  bgPanel: 'linear-gradient(165deg, rgba(30,40,66,0.60), rgba(12,17,30,0.55))',
  bgChart: '#070A12',
  bgInput: 'rgba(6,9,18,0.55)',
  divider: '#1B2233', // --line
  hairline: '#1B2233', // --line
  panelBorder: '#2A3346', // --line2
  inputBorder: '#2A3346', // --line2
  textPrimary: '#E6EAF2', // --hi
  textSecondary: '#8C96AC', // --mid
  textTertiary: '#566079', // --low
  textQuiet: '#3A4256', // --dis
  up: '#2CF5A7', // --bull
  down: '#FF5C87', // --bear
  bid: '#2CF5A7',
  ask: '#FF5C87',
  accent: '#2CF5A7', // --acc (brand green — D1 blue reverted, owner 2026-09-10)
  violet: '#8B7CFF', // --violet
  warn: '#FFB84D', // --warn
  live: '#2CF5A7', // brand green
  shadowPanel: '0 24px 60px -28px rgba(0,0,0,.65), inset 0 1px 0 rgba(255,255,255,0.05)',
  shadowFloat: '0 18px 50px rgba(0,0,0,.55), 0 0 0 1px rgba(150,175,225,0.08)',
};

// key -> backing CSS variable. Points straight at @quantum/tokens vars where
// one exists (plain hex — no var() indirection for getComputedStyle to
// resolve); the glass-identity surfaces still read their --gw-* alias.
const VAR = {
  bgOuter: '--void',
  bgPage: '--gw-bg-page',
  bgPanel: '--gw-bg-panel',
  bgChart: '--gw-bg-chart',
  bgInput: '--gw-bg-input',
  divider: '--line',
  hairline: '--line',
  panelBorder: '--line2',
  inputBorder: '--line2',
  textPrimary: '--hi',
  textSecondary: '--mid',
  textTertiary: '--low',
  textQuiet: '--dis',
  up: '--bull',
  down: '--bear',
  bid: '--bull',
  ask: '--bear',
  accent: '--acc',
  violet: '--violet',
  warn: '--warn',
  live: '--acc', // TODO(review): green --t-conn-ok may read better for a "live" dot
  shadowPanel: '--gw-shadow-panel',
  shadowFloat: '--shadow-float',
};

export const THEME = {
  ...DEFAULTS,
  // theme-independent
  radPanel: 14,
  radCard: 12,
  radCtrl: 9,
  radPill: 8,
  spring: 'cubic-bezier(0.3, 1.36, 0.44, 1)', // === --ease-spring
  fontUI: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif", // === --font-ui
  fontMono: "'JetBrains Mono', ui-monospace, 'SF Mono', monospace", // === --font-mono
};

function refreshTheme() {
  if (typeof window === 'undefined' || !document.documentElement) return;
  const cs = getComputedStyle(document.documentElement);
  for (const k in VAR) {
    const v = cs.getPropertyValue(VAR[k]).trim();
    if (v) THEME[k] = v;
  }
}

if (typeof window !== 'undefined') {
  refreshTheme();
  const mo = new MutationObserver(refreshTheme);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  requestAnimationFrame(refreshTheme);
}

// Group / cluster colors (watchlist tags, radar groups) — theme-independent
export const GROUP_COLORS = {
  green: '#2CF5A7', red: '#FF5C87', blue: '#4AA8FF',
  orange: '#FFB84D', yellow: '#FFE08A', purple: '#8B7CFF',
};

// uPlot / candle chart palette. Kept as getters so a chart that reads it
// per-frame follows the theme; a chart that destructures once still gets a
// sensible dark default.
export const CHART_THEME = {
  get bg() { return THEME.bgChart; },
  gridLine: 'rgba(150,175,225,0.06)',
  get axisText() { return THEME.textTertiary; },
  get up() { return THEME.up; },
  get down() { return THEME.down; },
  get wick() { return THEME.textTertiary; },
  priceLine: 'rgba(255,184,77,0.75)',
  priceTagBg: 'rgba(255,184,77,0.95)',
  priceTagText: '#1C1405',
  crossLine: 'rgba(234,240,250,0.32)',
  crossTagBg: 'rgba(234,240,250,0.94)',
  crossTagText: '#0A0E1A',
  get symbol() { return THEME.textPrimary; },
  get priceText() { return THEME.textPrimary; },
  get tfIdle() { return THEME.textTertiary; },
  get tfActive() { return THEME.bgChart; },
  get tfActiveBg() { return THEME.accent; },
  get pulse() { return THEME.accent; },
};

// Helper: subtle glass panel style for reuse
export const glassPanel = (extra = {}) => ({
  background: THEME.bgPanel,
  border: `1px solid ${THEME.panelBorder}`,
  borderRadius: THEME.radPanel,
  backdropFilter: 'blur(12px)',
  boxShadow: THEME.shadowPanel,
  ...extra,
});

// Helper: pick semantic color from a signed delta string/number
export const deltaColor = (v) => {
  const neg = typeof v === 'string' ? v.trim().startsWith('-') : v < 0;
  return neg ? THEME.down : THEME.up;
};

export default THEME;
